import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { authHeaders } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Breadcrumbs from '../components/Breadcrumbs';
import {
  Receipt, Plus, Loader2, X, Bot, CheckCircle2, XCircle, Clock,
  Send, Download, ChevronLeft, ChevronRight, Sparkles, IndianRupee, FileText,
  TrendingUp, TrendingDown, Building2, BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };

function SwipeCard({ invoice, onApprove, onReject, isTop }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-12, 12]);
  const approveOpacity = useTransform(x, [0, 100], [0, 1]);
  const rejectOpacity = useTransform(x, [-100, 0], [1, 0]);

  const handleDragEnd = (_, info) => {
    if (info.offset.x > 120) onApprove(invoice);
    else if (info.offset.x < -120) onReject(invoice);
  };

  return (
    <motion.div
      style={{ x, rotate, zIndex: isTop ? 10 : 1 }}
      drag={isTop ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.8}
      onDragEnd={handleDragEnd}
      initial={{ scale: isTop ? 1 : 0.95, y: isTop ? 0 : 8, opacity: isTop ? 1 : 0.7 }}
      animate={{ scale: isTop ? 1 : 0.95, y: isTop ? 0 : 8, opacity: isTop ? 1 : 0.7 }}
      exit={{ x: 300, opacity: 0, rotate: 15 }}
      className={`absolute inset-0 rounded-2xl border border-border bg-card shadow-lg overflow-hidden ${isTop ? 'cursor-grab active:cursor-grabbing' : ''}`}
      data-testid={`swipe-card-${invoice.id}`}
    >
      {/* Swipe indicators */}
      {isTop && (
        <>
          <motion.div style={{ opacity: approveOpacity }} className="absolute top-4 right-4 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-600 text-sm font-semibold z-10">
            Approve
          </motion.div>
          <motion.div style={{ opacity: rejectOpacity }} className="absolute top-4 left-4 px-3 py-1 rounded-full bg-red-500/20 text-red-600 text-sm font-semibold z-10">
            Reject
          </motion.div>
        </>
      )}

      <div className="p-6 h-full flex flex-col">
        {/* Invoice header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] text-muted-foreground font-mono">{invoice.invoice_number || 'INV-DRAFT'}</p>
            <h3 className="text-lg font-semibold font-heading mt-0.5">{invoice.to || 'Client'}</h3>
            <p className="text-[11px] text-muted-foreground">{invoice.date || new Date().toLocaleDateString('en-IN')}</p>
          </div>
          <Badge variant="secondary" className="text-[10px]">
            <Sparkles className="w-3 h-3 mr-1" strokeWidth={2} />
            AI Generated
          </Badge>
        </div>

        {/* Line items */}
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-2 mb-4">
            {(invoice.items || []).map((item, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{item.description}</p>
                  <p className="text-[10px] text-muted-foreground">Qty: {item.quantity} x ₹{Number(item.rate || 0).toLocaleString('en-IN')}</p>
                </div>
                <p className="text-sm font-heading font-medium ml-4">₹{Number(item.amount || 0).toLocaleString('en-IN')}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Totals */}
        <div className="border-t border-border pt-3 mt-auto">
          <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
            <span>Subtotal</span>
            <span>₹{Number(invoice.subtotal || 0).toLocaleString('en-IN')}</span>
          </div>
          {invoice.tax > 0 && (
            <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
              <span>GST (18%)</span>
              <span>₹{Number(invoice.tax || 0).toLocaleString('en-IN')}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-heading font-semibold">
            <span>Total</span>
            <span>₹{Number(invoice.total || 0).toLocaleString('en-IN')}</span>
          </div>
          {invoice.payment_terms && (
            <p className="text-[10px] text-muted-foreground/60 mt-2">{invoice.payment_terms}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function GenerateDialog({ onClose, onGenerated }) {
  const [client, setClient] = useState('');
  const [project, setProject] = useState('');
  const [description, setDescription] = useState('');
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    if (!client || !description) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API}/ai/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          tool: 'invoice',
          context: `Generate a professional invoice for client: ${client}. Project: ${project || 'General'}. Services: ${description}. Use Indian Rupees (INR). Include realistic line items, quantities, and rates for the Indian creative/film industry. Add 18% GST. Payment terms: Net 30 days.`
        }),
      });
      const data = await res.json();
      if (data.result) {
        const inv = typeof data.result === 'string' ? JSON.parse(data.result) : data.result;
        onGenerated({ ...inv, id: `inv_${Date.now()}`, status: 'draft', ai_generated: true });
        onClose();
      }
    } catch (err) {
      console.error('Invoice generation failed:', err);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="generate-dialog-overlay">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-lg mx-4"
        onClick={e => e.stopPropagation()}
        data-testid="generate-dialog"
      >
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-[hsl(36,80%,52%)]" strokeWidth={1.5} />
            <div>
              <h3 className="font-heading text-base font-semibold">Generate Invoice with Specter</h3>
              <p className="text-[10px] text-muted-foreground">AI-powered invoice generation</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} data-testid="generate-dialog-close">
            <X className="w-4 h-4" strokeWidth={2} />
          </Button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] text-muted-foreground font-medium mb-1 block">Client / Company *</label>
            <Input
              value={client}
              onChange={e => setClient(e.target.value)}
              placeholder="e.g., Dharma Productions"
              className="h-9 text-sm"
              data-testid="invoice-client-input"
            />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground font-medium mb-1 block">Project (optional)</label>
            <Input
              value={project}
              onChange={e => setProject(e.target.value)}
              placeholder="e.g., Echoes of Mumbai"
              className="h-9 text-sm"
              data-testid="invoice-project-input"
            />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground font-medium mb-1 block">Services / Description *</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="e.g., Cinematography services for 15 shoot days, including camera rental, crew, and color grading"
              className="w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-1 focus:ring-ring"
              data-testid="invoice-description-input"
            />
          </div>
        </div>

        <div className="p-5 border-t border-border flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} className="h-9">Cancel</Button>
          <Button
            size="sm"
            className="h-9 gap-2"
            onClick={generate}
            disabled={generating || !client || !description}
            data-testid="generate-invoice-btn"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />}
            {generating ? 'Generating...' : 'Generate Invoice'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

function LedgerView({ invoices, insight, insightLoading, onGenerateInsight }) {
  // Group by client
  const clientMap = {};
  invoices.forEach(inv => {
    const client = inv.to || inv.client || 'Unknown';
    if (!clientMap[client]) clientMap[client] = { total: 0, paid: 0, pending: 0, count: 0 };
    clientMap[client].count += 1;
    clientMap[client].total += (inv.total || 0);
    if (inv.status === 'approved' || inv.status === 'paid') clientMap[client].paid += (inv.total || 0);
    else clientMap[client].pending += (inv.total || 0);
  });
  const clients = Object.entries(clientMap).sort((a, b) => b[1].total - a[1].total);

  // Group by project
  const projectMap = {};
  invoices.forEach(inv => {
    const proj = inv.project_id || inv.project || 'General';
    if (!projectMap[proj]) projectMap[proj] = { total: 0, paid: 0, count: 0 };
    projectMap[proj].count += 1;
    projectMap[proj].total += (inv.total || 0);
    if (inv.status === 'approved' || inv.status === 'paid') projectMap[proj].paid += (inv.total || 0);
  });
  const projects = Object.entries(projectMap).sort((a, b) => b[1].total - a[1].total);

  const totalIncome = invoices.filter(i => i.status === 'approved' || i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0);
  const totalPending = invoices.filter(i => i.status !== 'approved' && i.status !== 'paid').reduce((s, i) => s + (i.total || 0), 0);
  const maxClientVal = clients.length > 0 ? clients[0][1].total : 1;

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }} data-testid="ledger-view">
      {/* Cash Flow Summary */}
      <motion.div variants={fadeUp} className="grid grid-cols-2 gap-4 mb-6">
        <div className="p-5 rounded-xl border border-border/50 bg-card" data-testid="ledger-income">
          <TrendingUp className="w-4 h-4 text-emerald-500 mb-2" strokeWidth={1.5} />
          <p className="font-heading text-2xl font-semibold text-emerald-600 dark:text-emerald-400">₹{(totalIncome / 100000).toFixed(1)}L</p>
          <p className="text-[10px] text-muted-foreground">Cash Received</p>
        </div>
        <div className="p-5 rounded-xl border border-border/50 bg-card" data-testid="ledger-pending">
          <TrendingDown className="w-4 h-4 text-amber-500 mb-2" strokeWidth={1.5} />
          <p className="font-heading text-2xl font-semibold text-amber-600 dark:text-amber-400">₹{(totalPending / 100000).toFixed(1)}L</p>
          <p className="text-[10px] text-muted-foreground">Pending / Outstanding</p>
        </div>
      </motion.div>

      {/* Specter Financial Insight */}
      <motion.div variants={fadeUp} className="mb-6">
        <div className="p-5 rounded-xl border border-border/50 bg-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Bot className="w-4 h-4 text-[hsl(36,80%,52%)]" strokeWidth={1.5} />
              <p className="text-sm font-medium">Specter Financial Insight</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5"
              onClick={onGenerateInsight}
              disabled={insightLoading}
              data-testid="generate-insight-btn"
            >
              {insightLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" strokeWidth={2} />}
              {insightLoading ? 'Analyzing...' : 'Generate Insight'}
            </Button>
          </div>
          {insight ? (
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line" data-testid="insight-text">{insight}</p>
          ) : (
            <p className="text-xs text-muted-foreground/50">Click "Generate Insight" to get Specter's analysis of your cash flow and invoices.</p>
          )}
        </div>
      </motion.div>

      {/* Client Breakdown */}
      <motion.div variants={fadeUp} className="mb-6">
        <p className="text-[11px] text-muted-foreground font-medium mb-3 flex items-center gap-1.5">
          <Building2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Client Breakdown
        </p>
        {clients.length > 0 ? (
          <div className="space-y-2">
            {clients.map(([name, data]) => (
              <div key={name} className="p-4 rounded-xl border border-border/50 bg-card" data-testid={`client-${name.replace(/\s/g, '-').toLowerCase()}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium">{name}</p>
                    <p className="text-[10px] text-muted-foreground">{data.count} invoice{data.count > 1 ? 's' : ''}</p>
                  </div>
                  <p className="text-sm font-heading font-semibold">₹{(data.total / 100000).toFixed(1)}L</p>
                </div>
                <div className="w-full h-2 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full rounded-full bg-foreground transition-all" style={{ width: `${(data.total / maxClientVal) * 100}%` }} />
                </div>
                <div className="flex justify-between mt-1.5 text-[9px] text-muted-foreground">
                  <span className="text-emerald-600 dark:text-emerald-400">₹{(data.paid / 1000).toFixed(0)}K paid</span>
                  <span className="text-amber-600 dark:text-amber-400">₹{(data.pending / 1000).toFixed(0)}K pending</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">No client data yet. Generate invoices to see breakdowns.</p>
        )}
      </motion.div>

      {/* Project Cash Flow */}
      <motion.div variants={fadeUp}>
        <p className="text-[11px] text-muted-foreground font-medium mb-3 flex items-center gap-1.5">
          <BarChart3 className="w-3.5 h-3.5" strokeWidth={1.5} /> Project Cash Flow
        </p>
        {projects.length > 0 ? (
          <div className="space-y-2">
            {projects.map(([name, data]) => (
              <div key={name} className="flex items-center gap-3 p-4 rounded-xl border border-border/50 bg-card" data-testid={`project-cashflow-${name.replace(/\s/g, '-').toLowerCase()}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{name}</p>
                  <p className="text-[10px] text-muted-foreground">{data.count} invoice{data.count > 1 ? 's' : ''} · ₹{(data.paid / 1000).toFixed(0)}K collected</p>
                </div>
                <p className="text-sm font-heading font-semibold flex-shrink-0">₹{(data.total / 100000).toFixed(1)}L</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">No project data yet.</p>
        )}
      </motion.div>
    </motion.div>
  );
}

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [savedInvoices, setSavedInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerate, setShowGenerate] = useState(false);
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'review'
  const [activeTab, setActiveTab] = useState('invoices'); // 'invoices' | 'ledger'
  const [insight, setInsight] = useState('');
  const [insightLoading, setInsightLoading] = useState(false);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await fetch(`${API}/invoices`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSavedInvoices(data);
      }
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleGenerated = (invoice) => {
    setInvoices(prev => [invoice, ...prev]);
    setViewMode('review');
  };

  const handleApprove = async (invoice) => {
    try {
      const res = await fetch(`${API}/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ...invoice, status: 'approved' }),
      });
      if (res.ok) {
        const saved = await res.json();
        setSavedInvoices(prev => [saved, ...prev]);
      }
    } catch {}
    setInvoices(prev => prev.filter(i => i.id !== invoice.id));
  };

  const handleReject = (invoice) => {
    setInvoices(prev => prev.filter(i => i.id !== invoice.id));
  };

  const allInvoices = [...savedInvoices];
  const filtered = filter === 'all' ? allInvoices : allInvoices.filter(i => i.status === filter);
  const totals = {
    total: allInvoices.reduce((s, i) => s + (i.total || 0), 0),
    approved: allInvoices.filter(i => i.status === 'approved').reduce((s, i) => s + (i.total || 0), 0),
    draft: allInvoices.filter(i => i.status === 'draft').reduce((s, i) => s + (i.total || 0), 0),
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }} data-testid="invoices-page">
      <Breadcrumbs />

      <motion.div variants={fadeUp} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight">Invoices & Ledger</h1>
          <p className="text-sm text-muted-foreground mt-1">Generate, review, and track cash flow with Specter AI.</p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'invoices' && invoices.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="gap-2 h-9"
              onClick={() => setViewMode(viewMode === 'review' ? 'list' : 'review')}
              data-testid="toggle-review-btn"
            >
              <FileText className="w-3.5 h-3.5" strokeWidth={2} />
              {viewMode === 'review' ? 'View List' : `Review (${invoices.length})`}
            </Button>
          )}
          <Button size="sm" className="gap-2 h-9" onClick={() => setShowGenerate(true)} data-testid="new-invoice-btn">
            <Plus className="w-4 h-4" strokeWidth={2} /> Generate Invoice
          </Button>
        </div>
      </motion.div>

      {/* Tab bar */}
      <motion.div variants={fadeUp} className="flex gap-1 p-1 rounded-lg bg-secondary/50 w-fit mb-6">
        {[
          { key: 'invoices', label: 'Invoices', icon: Receipt },
          { key: 'ledger', label: 'Ledger', icon: BarChart3 },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[13px] font-medium transition-all duration-150
              ${activeTab === tab.key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`tab-${tab.key}`}
          >
            <tab.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
            {tab.label}
          </button>
        ))}
      </motion.div>

      {activeTab === 'invoices' && (
        <>
          {/* Summary */}
          <motion.div variants={fadeUp} className="grid grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Total Value', value: totals.total, icon: IndianRupee },
              { label: 'Approved', value: totals.approved, icon: CheckCircle2 },
              { label: 'Drafts', value: totals.draft, icon: Clock },
            ].map(t => (
              <div key={t.label} className="p-4 rounded-xl border border-border/50 bg-card" data-testid={`invoice-summary-${t.label.toLowerCase().replace(/\s/g, '-')}`}>
                <t.icon className="w-4 h-4 text-muted-foreground mb-2" strokeWidth={1.5} />
                <p className="font-heading text-xl font-semibold">₹{(t.value / 100000).toFixed(1)}L</p>
                <p className="text-[10px] text-muted-foreground">{t.label}</p>
              </div>
            ))}
          </motion.div>

          {/* Swipe Review Mode */}
          <AnimatePresence>
            {viewMode === 'review' && invoices.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-8"
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] text-muted-foreground font-medium">Swipe to review ({invoices.length} pending)</p>
                  <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1"><ChevronLeft className="w-3 h-3" /> Reject</span>
                    <span className="flex items-center gap-1">Approve <ChevronRight className="w-3 h-3" /></span>
                  </div>
                </div>
                <div className="relative h-[420px] max-w-md mx-auto" data-testid="swipe-review-area">
                  <AnimatePresence>
                    {invoices.slice(0, 3).map((inv, i) => (
                      <SwipeCard
                        key={inv.id}
                        invoice={inv}
                        isTop={i === 0}
                        onApprove={handleApprove}
                        onReject={handleReject}
                      />
                    ))}
                  </AnimatePresence>
                  {invoices.length === 0 && (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      <p className="text-sm">All invoices reviewed!</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Filter */}
          <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="invoice-filter">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Invoices</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">{filtered.length} invoices</p>
          </motion.div>

          {/* Invoice List */}
          <motion.div variants={fadeUp}>
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
            ) : filtered.length > 0 ? (
              <div className="space-y-2">
                {filtered.map(inv => (
                  <div key={inv.invoice_id || inv.id} className="flex items-center gap-3 p-4 rounded-xl border border-border/50 bg-card hover:bg-secondary/30 transition-colors" data-testid={`invoice-${inv.invoice_id || inv.id}`}>
                    <Receipt className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{inv.to || inv.client || 'Invoice'}</p>
                        <span className="text-[10px] font-mono text-muted-foreground/50">{inv.invoice_number || ''}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        {inv.date || ''} · {(inv.items || []).length} items
                        {inv.ai_generated && <span className="ml-1.5 text-[hsl(36,80%,52%)]">AI</span>}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-heading font-medium">₹{Number(inv.total || 0).toLocaleString('en-IN')}</p>
                      <Badge
                        variant={inv.status === 'approved' ? 'default' : inv.status === 'sent' ? 'secondary' : 'outline'}
                        className="text-[9px] h-4 px-1.5 capitalize"
                      >
                        {inv.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 border border-dashed border-border rounded-xl">
                <Receipt className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-muted-foreground mb-1">No invoices yet</p>
                <p className="text-xs text-muted-foreground mb-4">Generate your first invoice with Specter AI</p>
                <Button size="sm" className="gap-2 h-9" onClick={() => setShowGenerate(true)} data-testid="empty-generate-btn">
                  <Sparkles className="w-3.5 h-3.5" strokeWidth={2} /> Generate Invoice
                </Button>
              </div>
            )}
          </motion.div>
        </>
      )}

      {/* CRM / Ledger View */}
      {activeTab === 'ledger' && (
        <LedgerView
          invoices={allInvoices}
          insight={insight}
          insightLoading={insightLoading}
          onGenerateInsight={async () => {
            setInsightLoading(true);
            try {
              const invSummary = allInvoices.map(i => ({ to: i.to || i.client, total: i.total, status: i.status, date: i.date }));
              const res = await fetch(`${API}/spectre/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                  message: `Analyze my invoice/cash flow data and give financial insights. Here are my invoices: ${JSON.stringify(invSummary)}. Provide: 1. Cash flow health summary, 2. Top client by revenue, 3. Payment collection rate, 4. One actionable recommendation. Keep it brief and professional.`,
                  session_id: 'ledger_insight',
                  context: 'financial_analysis'
                }),
              });
              const data = await res.json();
              setInsight(data.response || 'Unable to generate insight.');
            } catch { setInsight('Failed to generate insight. Please try again.'); }
            finally { setInsightLoading(false); }
          }}
        />
      )}

      {/* Generate Dialog */}
      <AnimatePresence>
        {showGenerate && (
          <GenerateDialog onClose={() => setShowGenerate(false)} onGenerated={handleGenerated} />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
